import { Trans, withI18n } from '@lingui/react'
import classNames from 'clsx'
import { node, oneOf, string } from 'prop-types'
import { Component } from 'react'
import { compose } from 'redux'

import { PAGE_TYPE_CATEGORY, PAGE_TYPES } from 'shared/consts/page'
import IconArrowRight from 'views/components/atoms/Icon/IconArrowRight'
import CmsInfoText from 'views/components/molecules/CmsInfoText'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import CmsNavigationList from 'views/components/organisms/CmsNavigationList'
import CmsNavigationSection from 'views/components/organisms/CmsNavigationSection'
import withMountPoints from 'views/enhancers/withMountPoints'
import {
  downNavTreeType,
  mountPointsType,
  navItemType,
  upNavTreeType,
} from 'views/utils/propTypes'

import BackToIndexLink from './BackToIndexLink'
import Logo from './Logo'
import './CmsLayout.scss'

class CmsLayout extends Component {
  static propTypes = {
    className: string,
    title: string,
    logoPath: string,
    topNav: navItemType,
    upNavTree: upNavTreeType,
    secondaryNavTree: downNavTreeType,
    secondaryNavTitle: string,
    infoText: string,
    slot: node,
    children: node.isRequired,
    pageType: oneOf(PAGE_TYPES).isRequired,
    path: string,
    mountPoints: mountPointsType.isRequired,
  }

  static defaultProps = {
    className: null,
    title: null,
    logoPath: null,
    topNav: null,
    upNavTree: [],
    secondaryNavTree: [],
    secondaryNavTitle: null,
    infoText: null,
    slot: null,
    path: null,
  }

  constructor(props) {
    super(props)

    const { upNavTree, secondaryNavTree } = props
    const hasNav = upNavTree.length !== 0 || secondaryNavTree.length !== 0

    this.state = { hasNav }
  }

  hasSidebarData() {
    const { logoPath, slot, infoText, pageType } = this.props
    const { hasNav } = this.state

    return (
      logoPath ||
      slot ||
      infoText ||
      (pageType === PAGE_TYPE_CATEGORY && hasNav)
    )
  }

  render() {
    const {
      className,
      title,
      logoPath,
      topNav,
      upNavTree,
      secondaryNavTree,
      secondaryNavTitle,
      infoText,
      slot,
      children: content,
      pageType,
      path,
      mountPoints,
    } = this.props
    const { hasNav } = this.state

    if (!this.hasSidebarData()) {
      return (
        <>
          <BackToIndexLink navItem={topNav} />
          {content}
        </>
      )
    }

    const sidebarClassName = classNames({
      'l-grid__item l-col--3-of-12@medium': true,
      'u-hidden@small-tiny': !slot,
    })
    const sidebarItemClassName = classNames({
      'u-margin-x-large--bottom': true,
      'u-hidden@small-tiny': slot,
    })

    let productListingLink = null
    if (path && pageType === PAGE_TYPE_CATEGORY) {
      productListingLink = (
        <div className="l-flex l-flex--justify-content--end@medium t-CmsLayout__all-products">
          <LinkWithIcon
            href={mountPoints.getCmsAllProductsFromCategory(path)}
            icon={<IconArrowRight />}
            right
          >
            <span className="u-font-weight--bold t-CmsLayout__all-products__text">
              <Trans id="general.label.category.AllProductsFromCategory" />
            </span>
          </LinkWithIcon>
        </div>
      )
    }

    return (
      <div
        className={classNames(
          'l-grid u-margin-x-large--bottom u-position--relative',
          className
        )}
        data-clientside-hook="cmsContent"
      >
        <div className={sidebarClassName}>
          {Boolean(logoPath) && (
            <Logo
              src={logoPath}
              alt={title ?? ''}
              className={sidebarItemClassName}
            />
          )}

          {!hasNav ? null : (
            <div className={sidebarItemClassName}>
              <CmsNavigationList
                upNavTree={upNavTree}
                secondaryNavTitle={secondaryNavTitle}
                secondaryNavTree={secondaryNavTree}
              />
            </div>
          )}
          {!slot ? null : (
            <div className="u-margin-x-large--bottom@medium">{slot}</div>
          )}
          <CmsInfoText className={sidebarItemClassName}>{infoText}</CmsInfoText>
        </div>
        <div className="l-grid__item l-col--9-of-12@medium">
          <BackToIndexLink navItem={topNav} className="u-hidden@medium" />
          {content}
          {!hasNav ? null : (
            <div className="u-hidden@medium">
              <CmsNavigationSection
                upNavTree={upNavTree}
                secondaryNavTitle={secondaryNavTitle || title}
                secondaryNavTree={secondaryNavTree}
              />
            </div>
          )}
          {productListingLink}
          {pageType !== PAGE_TYPE_CATEGORY ? null : (
            <CmsInfoText className="u-hidden@medium u-margin-x-large--top">
              {infoText}
            </CmsInfoText>
          )}
        </div>
      </div>
    )
  }
}

export default compose(withI18n(), withMountPoints)(CmsLayout)
